import type { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import type { TicketStatus } from "../Tickets/Ticketing.Enums";

/**
 * Routing groups and the centralized escalation/ticketing system. See
 * `docs/snowy/plans/2026-05-28-escalation-and-ticketing-plan.md` for Phase I
 * and `docs/snowy/plans/2026-XX-XX-routing-groups-phase-ii.md` for the
 * current Phase II model summarized below.
 *
 * A `RoutingGroup` is a named team (Sales, Service, Emergency, ...) that
 * handles a slice of escalation + ticketing work. The group owns:
 *  - `topics: StorageRoutingTopic[]` — which routing topics it handles at
 *    its scope. The resolver uses per-topic cascade (location > group >
 *    org) to pick the winning group for a given topic.
 *  - `roleSlugs: string[]` — which roles' members count as part of this
 *    group. Mirrors the legacy `EscalationGroupEntity.roles` shape.
 * A `RoutingGroupEvent` row records every escalation invocation for audit
 * + history.
 */

/**
 * Default schedule slug a routing group falls back to when its own
 * `scheduleSlug` is null — matches the Studio group-hours editor, where a group
 * with no explicit schedule honors the org's `call-center` hours (NOT
 * always-open). Single source of truth so the escalation resolver and the UI
 * can't drift. If this slug resolves to no schedule at any scope, the group is
 * treated as always-open (fail-open).
 */
export const DEFAULT_GROUP_SCHEDULE_SLUG = "call-center";

// ─── Entity interfaces (for controllers / API helper signatures) ────────────

/**
 * Shape of a `RoutingGroupEntity` row. Inheritance shape mirrors
 * `ScheduleInterface`: a row lives at exactly one of three scopes — org
 * (groupId null, locationId null), group, or location. Resolution via the
 * per-topic cascade in `ResolveRulesService`.
 */
export interface RoutingGroupInterface extends OrgBaseInterface {
  groupId: number | null;
  locationId: number | null;
  slug: string;
  name: string;
  description: string;
  isEnabled: boolean;
  /**
   * Routing topics this group handles at its scope. Per-topic cascade
   * resolution: the most-specific scope where some group's `topics`
   * contains the requested topic wins. Invariant: at any one scope, no
   * two non-archived groups share a topic — enforced at upsert.
   */
  topics: string[];
  /**
   * Role slugs whose holders are in this group. Mirrors
   * `EscalationGroupEntity.roles`. A member is "in" the group if they
   * have any of these roles at the scope of the location being routed,
   * AND their effective `escalationEligibility` (computed from role
   * default + per-permission override) is not `"none"`.
   */
  roleSlugs: string[];

  /** Top-level switch: ticket-only stops at the ticket; ticket-and-human also tries to reach someone. */
  defaultTicketAction: RoutingGroupTicketAction;

  // ─── Escalation-side (when defaultTicketAction === "ticket-and-human") ─────

  /** How to reach a human during business hours. */
  escalationMode: RoutingGroupEscalationMode;
  /**
   * When escalationMode is "call-center": ring everyone in the group simultaneously
   * (first to accept wins) vs ring one specifically picked agent. Null when escalationMode
   * is "call-forward" — not consulted.
   */
  escalationCallCenterDispatch: RoutingGroupEscalationDispatch | null;
  /**
   * When escalationCallCenterDispatch is "ring-one": how to pick the agent. Null otherwise.
   */
  escalationCallCenterPick: RoutingGroupPickStrategy | null;
  /**
   * Phone number to forward to when escalationMode is "call-forward" — the
   * in-hours / always-on forward target. Distinct from the outside-hours
   * fallback (`outsideHoursForwardPhoneNumber`) so the two paths can route
   * to different numbers (e.g., live operator vs after-hours voicemail).
   *
   * Fallback policy: if `outsideHoursForwardPhoneNumber` is null on a group
   * configured with `outsideHoursMode === "forward"`, the resolver falls
   * back to this `escalationForwardPhoneNumber` value (backward-compat
   * with single-number setups).
   */
  escalationForwardPhoneNumber: string | null;

  /**
   * Phone number to forward to when `outsideHoursMode === "forward"` — the
   * after-hours / closed-window fallback target. Independent of the
   * in-hours `escalationForwardPhoneNumber` so an operator can route
   * after-hours calls to voicemail / a different on-call number.
   *
   * Fallback: when null on an outside-hours forward group, the resolver
   * uses `escalationForwardPhoneNumber` instead. When BOTH are null and
   * the group is configured to forward, the resolver creates a
   * `routing-group-misconfig` ticket against the group's admins (so the
   * operator is alerted) and degrades the customer-facing path to
   * `openToGroup`.
   */
  outsideHoursForwardPhoneNumber: string | null;

  /**
   * Phone numbers (E.164) that receive an SMS alert when an escalation
   * lands on this routing group. Generic across routing groups; the
   * Studio UI only exposes the field on the Emergency page today. Empty
   * array = no alerts.
   */
  alertSmsRecipients: string[];

  /**
   * Email addresses that receive an email alert when an escalation lands
   * on this routing group. Same generic shape + emergency-only UI
   * surfacing as `alertSmsRecipients`.
   */
  alertEmailRecipients: string[];

  /**
   * Per-agent ring timeout (seconds): how long to ring ONE agent before
   * timing them out and rerouting to the next. Null = use the system
   * default (30s). Worst-case total wait = this × `escalationMaxAttempts`.
   * Only consulted when `escalationMode === "call-center"`.
   */
  escalationRingTimeoutSeconds: number | null;

  /**
   * Max agents tried before falling through to the ticket-assignment
   * branch. Null = use the system default (3).
   */
  escalationMaxAttempts: number | null;

  // ─── Ticket-side (always applies when a ticket is created) ─────────────────

  /** Open the ticket to the whole group (first claim wins) or assign to one specific person. */
  ticketAssignment: RoutingGroupTicketAssignment;
  /**
   * When ticketAssignment is "assign-to-one": how to pick the person. Null when
   * "open-to-group" — not consulted.
   */
  ticketAssignmentPick: RoutingGroupPickStrategy | null;

  // ─── Hours (via ScheduleEntity cascade) ──────────────────────────────────

  /**
   * Slug pointing into the ScheduleEntity table. Null = fall back to
   * `DEFAULT_GROUP_SCHEDULE_SLUG` (`"call-center"`), matching the Studio
   * group-hours editor. Resolved via
   * `ScheduleService.getOpenStatus(orgId, locationId, scheduleSlug)` which uses
   * `findCascaded` to walk location → group → org (org-level when no location).
   * If no schedule resolves at any scope, the group is treated as always-open.
   */
  scheduleSlug: string | null;
  /** Behavior when the resolved schedule says the team is closed. */
  outsideHoursMode: RoutingGroupOutsideHoursMode;

  /**
   * ENG-3089 — what the bot must know about the caller before it may file a
   * ticket to this group. Enforced server-side in the escalation handler after
   * the group is resolved (the model learns the requirement from the tool
   * result, not the schema — the group is only known once it picks a topic).
   * Platform default `any-contact` (Chuck, 2026-09-17). Operators may edit.
   */
  requiredContactInfo: RoutingGroupRequiredContactInfo;
  /**
   * ENG-3089 — what happens when the caller declines to share the required
   * info. `file-anyway` (default) files the ticket and notes the refusal in the
   * caller-context footer; `callback-only` files a ticket-only callback request
   * (never a live bridge); `do-not-file` files nothing and the bot offers other
   * options.
   */
  contactRefusalBehavior: RoutingGroupContactRefusalBehavior;
  // "Offer to help once before connecting" (ENG-3355) is NOT a per-group field
  // since ENG-3390 (Chuck, 2026-09-28): it lives on the scope-inherited
  // `LocationVarKey.CortexEscalationPushbackMode`. The group is only known once
  // `escalate_to_team` has run, so a per-group value could never inform the
  // prompt — and operators do not need per-group pushback.

  isArchived: boolean;
}

/**
 * A routing group the signed-in member belongs to, for the Tickets
 * "Group Inbox" nav rail. `unacceptedCount` is the number of tickets routed
 * to this group still `escalationState='open-to-group'` (awaiting a claim) —
 * the badge shown next to the group name.
 */
export interface MyRoutingGroup {
  slug: string;
  name: string;
  unacceptedCount: number;
}

/** Shape of a `RoutingGroupEventEntity` row. */
export interface RoutingGroupEventInterface extends OrgBaseInterface {
  botConversationId: number | null;
  ticketId: number | null;
  locationId: number | null;
  routingTopic: string | null;
  channel: "voice" | "web" | "sms" | null;
  outcome: HandleEscalationOutcome;
  resolvedGroupId: number | null;
  resolvedGroupSlug: string | null;
  resolvedTicketAction: string | null;
  resolvedHumanMode: string | null;
  forwardPhoneNumber: string | null;
  ccv3AssignmentId: number | null;
  ccv3Result: string | null;
  trace: Record<string, unknown> | null;
}

/**
 * `RoutingGroupEventInterface` plus the CURRENT state of the referenced
 * ticket, joined server-side by `listEvents`. Lets the Recent Activity
 * UI show "Processing" / "Claimed by …" / "Open to group" / "Closed"
 * alongside the original outcome — the outcome captures what happened at
 * dispatch time; the ticket state shows where it landed after.
 */
export interface RoutingGroupEventWithTicketState extends RoutingGroupEventInterface {
  ticketEscalationState: TicketEscalationState | null;
  ticketClaimedByMemberId: number | null;
  /** Underlying ticket workflow status — used to distinguish "closed" from in-progress states. */
  ticketWorkflowStatus: string | null;
}

// ─── Group-level setting enums ───────────────────────────────────────────────

/** Whether an escalation creates only a ticket, or also tries to reach a human. */
export type RoutingGroupTicketAction = "ticket-only" | "ticket-and-human";

/** How to reach a human for a live escalation: in-app via CC-v3, or transfer the call. */
export type RoutingGroupEscalationMode = "call-center" | "call-forward";

/** When call-center: ring everyone vs ring one specifically picked agent. */
export type RoutingGroupEscalationDispatch = "ring-everyone" | "ring-one";

/** Who handles a ticket: open to the whole group, or assign to one specific person. */
export type RoutingGroupTicketAssignment = "open-to-group" | "assign-to-one";

/**
 * How to pick a single agent / assignee when only one is needed (used by
 * `escalationCallCenterPick` when escalationCallCenterDispatch is "ring-one"
 * AND by `ticketAssignmentPick` when ticketAssignment is "assign-to-one").
 */
export type RoutingGroupPickStrategy = "round-robin" | "least-busy";

/** What happens to an escalation that arrives outside the group's configured hours. */
export type RoutingGroupOutsideHoursMode = "queue" | "forward" | "reject";

/**
 * ENG-3089 — contact info the bot must hold before filing a ticket to a group.
 *  - `any-contact`: a way to reach the caller — phone OR email (platform default;
 *    voice/SMS satisfy it via caller ID, web via the profile form / OTP).
 *  - `name`: the caller's name.
 *  - `name-and-contact`: name AND a phone or email.
 *  - `none`: file with whatever is known.
 */
export type RoutingGroupRequiredContactInfo = "any-contact" | "name" | "name-and-contact" | "none";

/** ENG-3089 — what the handler does when the caller declines to share the required info. */
export type RoutingGroupContactRefusalBehavior = "file-anyway" | "callback-only" | "do-not-file";

/** The individual facts the contact gate can report as missing. */
export type ContactGateField = "name" | "contact";

/**
 * ENG-3355 — before connecting a caller to the team:
 *  - `connect-right-away`: an explicit request escalates on the first ask (legacy behavior).
 *  - `offer-help-once`: the first attempt is pushed back — the bot offers to help itself once;
 *    the second attempt goes through.
 *  - `offer-help-once-unless-frustrated` (platform default): as above, unless the customer is
 *    frustrated or angry — then the first attempt goes through.
 */
export type RoutingGroupPushbackMode = "connect-right-away" | "offer-help-once" | "offer-help-once-unless-frustrated";
// ^ Since ENG-3390 this is the value set of `LocationVarKey.CortexEscalationPushbackMode`
//   (org → group → location cascade, platform default offer-help-once-unless-frustrated),
//   no longer a routing-group column. The name is kept to avoid churn.

// ─── Role-based eligibility (replaces the Phase I per-member memberships) ───

/**
 * Tri-state on `RoleEntity.escalationEligibility` and (optionally) on each
 * `MemberPermission` entry as a per-assignment override. See
 * `effectiveEscalationEligibility` in `libs/helpers/Permissions/`.
 *
 * - `"all"` — Member can be live-dispatched (CC-v3 ring) AND assigned tickets.
 * - `"tickets-only"` — Member CAN be assigned tickets / claim from the inbox,
 *   but is NEVER a candidate for live dispatch. Use case: back-office staff,
 *   async-only managers.
 * - `"none"` — Excluded from both paths regardless of role-slug match.
 *
 * `null` on either layer means "unset; fall through" (role-default uses the
 * `parentSlug` chain; per-permission override falls through to the role's
 * resolved default). Ultimate fallback when everything is null is `"all"`.
 */
export type RoleEscalationEligibility = "all" | "tickets-only" | "none";

// ─── Ticket lifecycle (escalation acceptance phase) ──────────────────────────

/**
 * Tracks the escalation-acceptance phase of a ticket. Separate from the
 * existing workflow `status` (TODO / IN_PROGRESS / etc.) — that's about
 * the work being done; this is about who's responsible.
 *
 * - `normal`: not part of an escalation flow, OR escalation already resolved
 *             (forwarded-and-closed, etc.). Existing-ticket default.
 * - `processing`: centralized handler is figuring out where it goes.
 *                 Transient — invisible to inbox views. Lasts at most a
 *                 few seconds (until CC-v3 reports back or no-agent fallback fires).
 * - `open-to-group`: ticket is claimable by anyone in the assigned group.
 *                    Notification fires to all group members.
 * - `claimed`: a specific member has accepted (or was directly assigned
 *              for single-person mode). Locked.
 */
export type TicketEscalationState = "normal" | "processing" | "open-to-group" | "claimed";

// ─── Centralized handler input/output ────────────────────────────────────────

/**
 * Input to `handleEscalation`. Bot tools pass this when invoking the
 * centralized escalation function.
 */
export interface HandleEscalationInput {
  orgId: number;
  /** Null + undefined both mean "no location in scope" (org-only routing). */
  locationId?: number | null;
  /**
   * Operational routing dimension the bot classified into (domain-scoped
   * — `StorageRoutingTopic` for storage). Drives which routing group +
   * per-topic overrides apply.
   */
  routingTopic?: string | null;
  channel: "voice" | "web" | "sms";
  /**
   * Conversation row id for routing-event audit. Null is permitted for
   * out-of-band callers (admin tools, tests) but production bot tools
   * always have one — it's set when the conversation row is created.
   */
  botConversationId: number | null;
  /** Free-form reason from the bot's `escalate_to_team` `reason` arg. */
  reason?: string | null;
  /** Ticket content the bot has already prepared. */
  ticketSeed: {
    title: string;
    description: string | null;
    userId?: number;
    tags?: string[];
    /**
     * The follow-up the user asked for, as an ISO 8601 instant, when the bot captured one
     * (ENG-3082; Shannon 2026-08-21: schema-additive, optional, no automation on it). Becomes the
     * ticket's `dueDate` on create; an update to an existing ticket does not move its due date.
     */
    dueDate?: string | null;
  };
  /**
   * When true, the handler skips the human-handoff branches (call-center
   * dispatch, call-forward) regardless of what the resolved rules say and
   * finalizes the ticket as ticket-only. Used by:
   *   - `escalate_to_team` when the customer hasn't consented to a live
   *     bridge OR the call center is outside hours.
   *   - `report_emergency` and `forward_call` when the tool owns its own
   *     dispatch logic and only wants the ticket + routing-event tracking.
   */
  forceTicketOnly?: boolean;
  /**
   * TKT-LL (ENG-2578) — bypass the LLM-judge ticket-update path entirely
   * and always create a fresh ticket. Set by `report_emergency` so that
   * an emergency can never be silently rolled into a non-emergency open
   * ticket (which would be a hallucinated-successful-escalation failure:
   * SMS/email alert goes out + a ticket "exists" but it isn't actually
   * the emergency-tagged record the operator needs). Operators need each
   * emergency surfaced separately in the inbox.
   */
  bypassUpdate?: boolean;
  /**
   * ENG-3089 — contact facts the bot collected in conversation (the tool's
   * `customerName` / `customerPhone` / `customerEmail`) plus whether the caller
   * explicitly declined to share what the group requires. Merged server-side
   * with the user record and `callerContext.callerPhone` before the contact
   * gate runs. All optional — absent means "nothing collected".
   */
  contact?: {
    name?: string;
    phone?: string;
    email?: string;
    /** True only when the bot asked for the required info and the caller declined. */
    declined?: boolean;
  };
  /**
   * ENG-3355 — signals for the pushback decision, computed bot-side
   * (deterministic empathy-cue patterns + the model's `customerFrustrated`
   * flag). `exemptReason` set = never push back (death/estate, profanity →
   * transfer, safety). The server owns the attempt counter and the mode.
   */
  pushback?: {
    customerFrustrated?: boolean;
    exemptReason?: string;
    /**
     * ENG-3390 — the prompt already had the bot offer to help once on the
     * first ask (the scope's mode was known at prompt build), so this call is
     * the caller's second ask. Counts as an attempt already made: the server
     * connects instead of pushing back a second time.
     */
    priorOfferMade?: boolean;
  };
  /** ENG-3355 — bypass the pushback check (report_emergency, forward_call own their dispatch). */
  skipPushback?: boolean;
  /**
   * ENG-3089 — bypass the contact gate entirely. Set by `report_emergency`
   * (life-safety never waits on a name) and `forward_call` (the call itself
   * is the deliverable). The emergency routing group is exempt regardless.
   */
  skipContactGate?: boolean;
  /**
   * Structured caller facts the bot side already knows — used by
   * `createProcessingTicket` to append a deterministic "Caller context"
   * footer to every bot-created ticket (caller-context plan, 2026-06-11).
   * Deliberately NOT part of `ticketSeed`: the seed is LLM-authored
   * content; this is system-known fact and must never depend on the
   * model choosing to include it.
   */
  callerContext?: {
    /** Caller-ID / SMS sender as dialed — from ToolContext.userPhoneNumber. */
    callerPhone?: string;
    /** Web only: page the visitor was on at escalation (latest PAGE_NAVIGATION row). */
    pageUrl?: string;
  };
}

export type HandleEscalationOutcome =
  | "dispatching"
  | "forward"
  | "ticket-only"
  | "no-agent-available"
  /**
   * Outside-hours mode `reject` fired — no ticket created, no human
   * dispatch. Returned so the bot can apologize and offer self-serve.
   */
  | "rejected-outside-hours"
  /**
   * ENG-3089 — the resolved group requires contact info the bot does not have
   * yet (or the caller declined and the group is set to `do-not-file`). No
   * ticket created. `missingContactFields` says what to collect; the bot asks
   * and calls again.
   */
  | "rejected-missing-contact"
  /**
   * ENG-3355 — the resolved group wants the bot to offer help once before
   * connecting, and this was the conversation's first escalation attempt. No
   * ticket, no dispatch. The bot offers to help; the next attempt goes through.
   */
  | "pushback"
  /**
   * TKT-KK (ENG-2577) — the LLM judge decided the bot's request is a
   * continuation of an existing open ticket for this caller. The new
   * context was appended to that ticket; no new routing fired (the
   * existing ticket is already routed and being worked). Bot must tell
   * the customer "our team is already aware of this."
   */
  | "updated-existing";

export interface HandleEscalationResult {
  outcome: HandleEscalationOutcome;
  /** Null when outcome is "rejected-outside-hours" or "rejected-missing-contact" — no ticket was created. */
  ticketId: number | null;
  /** Null when no ticket was created (see `ticketId`). Sequential per-org ticket number for customer-facing display. */
  ticketNumber: number | null;
  /**
   * ENG-3089 — set when outcome === "rejected-missing-contact": which facts the
   * group still needs before a ticket can be filed.
   */
  missingContactFields?: ContactGateField[];
  /** ENG-3089 — the resolved group's requirement, echoed so the bot can word its ask. */
  requiredContactInfo?: RoutingGroupRequiredContactInfo;
  /**
   * ENG-3089 — true when the caller declined the required info and the group's
   * refusal behavior decided the outcome (`do-not-file` → rejected-missing-contact;
   * `callback-only` → ticket-only; `file-anyway` → normal flow, refusal noted).
   */
  contactDeclined?: boolean;
  /** ENG-3355 — set when outcome === "pushback": the group's mode, for the bot's wording. */
  pushbackMode?: RoutingGroupPushbackMode;
  /** ENG-3355 — this conversation's escalation attempt number (1 = first), when counted. */
  escalationAttempt?: number;
  /**
   * The resolved routing group's current open-status, evaluated against the
   * group's own `scheduleSlug`. Returned so the bot words its offer / "team is
   * closed" copy from the right schedule instead of re-checking the hardcoded
   * call-center slug. Undefined only on early returns that predate group
   * resolution.
   */
  groupOpen?: boolean;
  /** When the group is closed: the next time it opens (ISO), for "back at X" copy. */
  nextOpenAt?: string | null;
  /** IANA timezone the group's hours were evaluated in. */
  timezone?: string;
  /** Set when outcome === "forward". */
  forwardPhoneNumber?: string;
  /** Set when outcome === "dispatching" — CC-v3 returned an assignment id. */
  assignmentId?: number;
  /** Set when outcome === "no-agent-available" or "ticket-only" — the slug the ticket is open to. */
  routingGroupSlug?: string;
  /** Optional human-readable note returned when outcome === "rejected-outside-hours" (e.g. nextOpenAt hint). */
  rejectedReason?: string;
  /**
   * TKT-KK — when outcome === "updated-existing", the title of the ticket
   * that got the new context. Bot uses this in its customer-facing
   * acknowledgment ("Our team is already aware of <existingTicketTitle>…").
   */
  existingTicketTitle?: string;
  /**
   * TKT-KK — when outcome === "updated-existing", the existing ticket's
   * status (TODO / IN_PROGRESS). Lets the bot say "the team is already
   * working on this" if IN_PROGRESS vs. "we have it logged" if TODO.
   */
  existingTicketStatus?: TicketStatus;
  /**
   * TKT-KK — when outcome === "updated-existing", the existing ticket's
   * creation timestamp. Bot may use it to phrase "your call from
   * yesterday / earlier today / etc." but the canonical phrasing is
   * "your earlier note" so this is optional context, not required.
   */
  existingTicketCreatedAt?: string;
  /**
   * ENG-2734 — when outcome === "updated-existing", the caller's own logged
   * updates on the matched ticket (oldest→newest; `BOT_UPDATED_TICKET`
   * `appendedSummary` values). Customer-safe by construction (bot-authored
   * only — no operator notes / editable description). Lets the bot read the
   * CURRENT state back ("your tour is now 4 PM") instead of the stale title.
   */
  existingTicketUpdates?: string[];
  /**
   * TKT-KK — when outcome === "updated-existing", the judge's reason +
   * confidence so the audit trail in the conversation event message
   * matches the audit trail on the ticket activity row.
   */
  judgeReason?: string;
  judgeConfidence?: number;
}

// ─── Resolution preview (used by PM page + studio create-ticket modal) ──────

/**
 * Result of `GET /routing/org/:orgId/preview` — "if an escalation about
 * topic X came in right now at location Y, what would happen?" Used by the
 * Routing Preview section on the PM page and by the CreateTicketModal to
 * show operators which group a manually-created ticket will land in.
 */
export interface RoutingPreviewResult {
  resolvedGroup: {
    slug: string;
    name: string;
    memberIds: number[];
  } | null;
  effective: {
    ticketAction: RoutingGroupTicketAction;
    escalationMode: RoutingGroupEscalationMode;
    escalationCallCenterDispatch: RoutingGroupEscalationDispatch | null;
    escalationCallCenterPick: RoutingGroupPickStrategy | null;
    escalationForwardPhoneNumber: string | null;
    outsideHoursForwardPhoneNumber: string | null;
    alertSmsRecipients: string[];
    alertEmailRecipients: string[];
    escalationRingTimeoutSeconds: number | null;
    escalationMaxAttempts: number | null;
    ticketAssignment: RoutingGroupTicketAssignment;
    ticketAssignmentPick: RoutingGroupPickStrategy | null;
    outsideHoursMode: RoutingGroupOutsideHoursMode;
    requiredContactInfo: RoutingGroupRequiredContactInfo;
    contactRefusalBehavior: RoutingGroupContactRefusalBehavior;
    /** ENG-3390 — resolved from `CortexEscalationPushbackMode` at this scope, not from the group. */
    escalationPushbackMode: RoutingGroupPushbackMode;
  };
  /** Cascade source per setting, e.g. "group-default" | "topic-override" | "scope-override". */
  inheritance: Record<string, "group-default" | "topic-override" | "scope-override">;
}
